import React, { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import {
  BriefcaseIcon,
  BuildingOffice2Icon,
  AcademicCapIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  CalendarDaysIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

import {
  ROLE_ADMIN,
  ROLE_COLLABORATOR_ADMIN,
  ROLE_COLLABORATOR_EDITOR,
  ROLE_COLLABORATOR_VIEWER,
  useAuth
} from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components';
import { Collaborator } from '@/models';
import { translateActivityStatus } from '@/utils/collaborator-status';
import { db } from '@/config/firebase';
import { doc, getDoc } from 'firebase/firestore';

type CollaboratorsLayoutContext = {
  collaborators: Collaborator[];
  isLoading: boolean;
  error: Error | null;
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
};

const formatBoolean = (value?: boolean | null) => {
  if (value === undefined || value === null) return '-';
  return value ? 'Sim' : 'Não';
};

const formatWorkModels = (models?: string[] | null) => {
  if (!models || models.length === 0) return '-';
  return models.join(', ');
};

const formatCurrency = (value?: number | null) => {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatList = (items?: string[] | null) => {
  if (!items || items.length === 0) return '-';
  return items.join(', ');
};

const roleTypeLabel: Record<string, string> = {
  administrative: 'Função administrativa',
  operational: 'Função operacional',
};

const getDepartmentLabel = (collaborator: Collaborator) => {
  const options = [
    (collaborator as any).departmentName,
    (collaborator as any).department?.name,
    (collaborator as any).department?.title,
    (collaborator as any).department,
  ];
  return options.find((item) => item && String(item).trim().length) || '-';
};

export default function CollaboratorContractPage() {
  const { collaborators, isLoading, error } = useOutletContext<CollaboratorsLayoutContext>();
  const { id } = useParams<{ id: string }>();
  const { user, userHasRole } = useAuth();
  const [departmentLabel, setDepartmentLabel] = useState<string | null>(null);
  const [isDepartmentLoading, setIsDepartmentLoading] = useState(false);

  const collaborator = useMemo(
    () => collaborators.find((item) => item.id === id),
    [collaborators, id],
  );

  useEffect(() => {
    let cancelled = false;
    const resolved = collaborator ? getDepartmentLabel(collaborator) : null;
    if (resolved && resolved !== '-') {
      setDepartmentLabel(resolved);
      return;
    }
    const fetchDepartment = async () => {
      if (!collaborator?.departmentId) return;
      setIsDepartmentLoading(true);
      try {
        const snap = await getDoc(doc(db, 'departments', collaborator.departmentId));
        if (!snap.exists()) return;
        const data = snap.data() as { completeDescription?: string; name?: string; title?: string };
        const label = data.completeDescription || data.name || data.title || null;
        if (!cancelled && label) setDepartmentLabel(label);
      } catch (err) {
        console.error('[CollaboratorContractPage] Failed to fetch department', err);
      } finally {
        if (!cancelled) setIsDepartmentLoading(false);
      }
    };
    fetchDepartment();
    return () => {
      cancelled = true;
    };
  }, [collaborator]);

  const canSeePage =
    userHasRole(ROLE_ADMIN) ||
    userHasRole(ROLE_COLLABORATOR_ADMIN) ||
    userHasRole(ROLE_COLLABORATOR_EDITOR) ||
    userHasRole(ROLE_COLLABORATOR_VIEWER) ||
    (user?.collaboratorId && user.collaboratorId === id);

  if (!canSeePage) {
    return (
      <div className="rounded-xl border border-amber-100 bg-amber-50 p-6 text-amber-900">
        Você não tem permissão para visualizar os detalhes deste colaborador.
      </div>
    );
  }

  if (isLoading && !collaborators.length) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-100 bg-red-50 p-6 text-red-800">
        Não foi possível carregar o colaborador.
      </div>
    );
  }

  if (!collaborator) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-6 text-gray-700 shadow-sm">
        Colaborador não encontrado.
      </div>
    );
  }

  const contractSections = [
    {
      title: 'Vínculo',
      description: 'Status e datas principais',
      icon: CalendarDaysIcon,
      items: [
        { label: 'Status', value: translateActivityStatus(collaborator.status) },
        { label: 'Tipo de colaborador', value: collaborator.collaboratorType || '-' },
        { label: 'Data de início', value: formatDate(collaborator.startDate) },
        { label: 'Data de término', value: formatDate(collaborator.endDate) },
        ...(collaborator.terminationReason
          ? [{ label: 'Motivo do desligamento', value: collaborator.terminationReason }]
          : []),
        { label: 'Fim do período de experiência', value: formatDate((collaborator as any).experienceEndDate) },
        ...(collaborator.collaboratorType === 'Fornecedor'
          ? [{ label: 'Direito a descanso remunerado', value: formatBoolean(collaborator.hasPaidRest) }]
          : []),
      ],
    },
    {
      title: 'Cargo e função',
      description: 'Posição e responsabilidade',
      icon: BriefcaseIcon,
      items: [
        { label: 'Cargo', value: collaborator.positionName || '-' },
        { label: 'Senioridade', value: collaborator.seniorityName || '-' },
        { label: 'Tipo de função', value: collaborator.roleType ? roleTypeLabel[collaborator.roleType] || collaborator.roleType : '-' },
        { label: 'Carga horária', value: collaborator.workSchedule ? `${collaborator.workSchedule}h/semana` : '-' },
      ],
    },
    {
      title: 'Regime de trabalho',
      description: 'Preferências e localização',
      icon: ClockIcon,
      items: [
        { label: 'Preferência de trabalho', value: formatWorkModels(collaborator.preferredWorkModels as string[]) },
        { label: 'Ex-aluno Raro Academy', value: formatBoolean((collaborator as any).isRaroAcademyAlumnus) },
      ],
    },
    {
      title: 'Responsáveis',
      description: 'Gestão e ponto focal',
      icon: UserGroupIcon,
      items: [
        {
          label: 'Departamento',
          value: departmentLabel || (isDepartmentLoading ? 'Carregando...' : '-'),
        },
        { label: 'Responsável raro', value: (collaborator as any).responsibleName || '-' },
        { label: 'Responsável no cliente', value: (collaborator as any).clientResponsible || '-' },
      ],
    },
    {
      title: 'Fornecedor (quando aplicável)',
      description: 'Dados da empresa terceirizada',
      icon: BuildingOffice2Icon,
      items: [
        { label: 'Empresa', value: (collaborator as any).companyName || '-' },
        { label: 'CNPJ', value: (collaborator as any).cnpj || '-' },
      ],
    },
    {
      title: 'Estágio',
      description: 'Dados do contrato de estágio',
      icon: AcademicCapIcon,
      items: [
        { label: 'Instituição de ensino', value: (collaborator as any).internshipEducationalInstitution || '-' },
        { label: 'Curso', value: (collaborator as any).internshipCourse || '-' },
        { label: 'Contrato de estágio', value: (collaborator as any).internshipContractFile ? 'Enviado' : '-' },
      ],
    },
    {
      title: 'Benefícios (contratação)',
      description: 'Benefícios associados ao vínculo',
      icon: DocumentTextIcon,
      items: [
        { label: 'Valor transporte', value: formatCurrency((collaborator as any).transportationBenefitAmount) },
        { label: 'Caju', value: formatBoolean((collaborator as any).hasCajuBenefit) },
        { label: 'Wellhub (Gympass)', value: formatBoolean((collaborator as any).hasWellhubBenefit) },
        { label: 'Guapeco', value: formatBoolean((collaborator as any).hasGuapecoBenefit) },
        { label: 'Plano funerário (Zelo)', value: formatBoolean((collaborator as any).hasZeloBenefit) },
        { label: 'Dependentes Zelo', value: formatList((collaborator as any).zeloDependents as string[]) },
      ],
    },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Detalhes de Contratação</h2>
          <p className="text-sm text-slate-500">Informações contratuais e funcionais</p>
        </div>
        <Link
          to={`/collaborators/${collaborator.id}`}
          className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Voltar para o perfil
        </Link>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {contractSections.map((section) => (
          <section
            key={section.title}
            className="group relative flex flex-col rounded-2xl border border-slate-100 bg-white p-5 shadow-sm ring-1 ring-slate-100 transition duration-300 hover:shadow-md"
          >
            <div className="relative flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                  <section.icon className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-900">
                    {section.title}
                  </p>
                  {section.description ? (
                    <p className="text-xs text-slate-500">{section.description}</p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="relative flex-1">
              <dl className="space-y-3">
                {section.items.map((item) => (
                  <div key={item.label} className="flex flex-col gap-1">
                    <dt className="text-xs font-medium text-slate-500">
                      {item.label}
                    </dt>
                    <dd className="text-sm font-semibold text-slate-900">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
